/**
 * /fpa/budget — Budget Agent: conversational budget authoring with live grid sidebar.
 *
 * Layout:
 *   Left  — chat interview (founder types, agent responds, coverage updates)
 *   Right — live budget grid: covered accounts, running totals, open accounts
 *
 * The agent is write-capable (gated by fpa.budget.manage). Every number it
 * proposes comes from budget_line_compute() on the backend — the LLM never
 * types a dollar figure. The sidebar renders only code-computed data.
 *
 * Session memory: on first open the UI fetches the session greeting from
 * GET /api/fpa/budget/session/:year — if a session exists, the greeting
 * says "Welcome back — 6 of 14 accounts, $840k so far." The founder never
 * loses progress.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { api, errorMessage } from "@/lib/api";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/fpa/budget")({
  component: BudgetAgentPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Coverage {
  covered: number;
  total: number;
  total_budgeted: string;
  open_accounts: string[];
  stage: string;
}

interface AgentReply {
  reply: string;
  coverage: Coverage;
  session_id: string;
  error?: string;
}

interface SessionState {
  greeting: string;
  session: {
    id: string | null;
    status: string;
    state: Record<string, unknown>;
  };
}

interface BvaLine {
  account_name: string;
  account_type: string;
  period_month: number;
  actual: string;
  budget: string;
  variance: string;
  variance_pct: string;
}

type Turn =
  | { kind: "agent"; text: string }
  | { kind: "user"; text: string }
  | { kind: "system"; text: string };

// ── Page ──────────────────────────────────────────────────────────────────────

function BudgetAgentPage() {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(currentYear);
  const [transcript, setTranscript] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();

  // Load session on mount / year change
  const sessionQuery = useQuery<SessionState>({
    queryKey: ["budget-session", fiscalYear],
    queryFn: () => api<SessionState>(`/api/fpa/budget/session/${fiscalYear}`),
    staleTime: 60_000,
  });

  // Budget-vs-actual for the sidebar bottom panel
  const bvaQuery = useQuery({
    queryKey: ["budget-vs-actual", fiscalYear],
    queryFn: () => api<{ has_budget: boolean; lines: BvaLine[]; net_income_actual: string; net_income_budget: string; variance: string }>(
      `/api/fpa/budget-vs-actual/${fiscalYear}`
    ),
    staleTime: 30_000,
    enabled: !!coverage?.covered,
  });

  // Seed greeting on session load
  useEffect(() => {
    if (sessionQuery.data && transcript.length === 0) {
      setTranscript([{ kind: "agent", text: sessionQuery.data.greeting }]);
      if (sessionQuery.data.session.state.covered_count !== undefined) {
        const s = sessionQuery.data.session.state as Record<string, unknown>;
        setCoverage({
          covered: Number(s.covered_count ?? 0),
          total: Number(s.total_count ?? 0),
          total_budgeted: String(s.total_budgeted ?? "0"),
          open_accounts: (s.open_account_ids as string[] ?? []).slice(0, 6),
          stage: String(s.stage ?? ""),
        });
      }
    }
  }, [sessionQuery.data]);

  // Auto-scroll on new turn
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    inputRef.current?.focus();
  }, [transcript.length]);

  const sendMessage = useMutation({
    mutationFn: (message: string) =>
      api<AgentReply>("/api/fpa/budget/chat", {
        method: "POST",
        body: {
          message,
          fiscal_year: fiscalYear,
          session_id: sessionId ?? undefined,
        },
      }),
    onSuccess: (data) => {
      if (data.error) {
        setTranscript((t) => [...t, { kind: "system", text: `Error: ${data.error}` }]);
        return;
      }
      setTranscript((t) => [...t, { kind: "agent", text: data.reply }]);
      setCoverage(data.coverage);
      setSessionId(data.session_id);
      qc.invalidateQueries({ queryKey: ["budget-session", fiscalYear] });
      qc.invalidateQueries({ queryKey: ["budget-vs-actual", fiscalYear] });
    },
    onError: (err) => {
      toast.error(errorMessage(err));
    },
  });

  const activate = useMutation({
    mutationFn: () =>
      api("/api/fpa/budget/activate", {
        method: "POST",
        body: { fiscal_year: fiscalYear },
      }),
    onSuccess: () => {
      toast.success("Budget activated — it's now your plan for the year.");
      qc.invalidateQueries({ queryKey: ["budget-vs-actual", fiscalYear] });
      qc.invalidateQueries({ queryKey: ["budget-session", fiscalYear] });
      setTranscript((t) => [...t, {
        kind: "system",
        text: `✓ Budget activated for ${fiscalYear}. The CFO can now compare your actuals against plan.`,
      }]);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  async function onSubmit() {
    const msg = draft.trim();
    if (!msg || sendMessage.isPending) return;
    setDraft("");
    setTranscript((t) => [...t, { kind: "user", text: msg }]);
    sendMessage.mutate(msg);
  }

  const isComplete = coverage?.stage === "done";
  const pct = coverage && coverage.total > 0
    ? Math.round((coverage.covered / coverage.total) * 100)
    : 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Budget Agent
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Build your {fiscalYear} plan in conversation — every number comes from the ledger.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={fiscalYear}
            onChange={(e) => {
              setFiscalYear(Number(e.target.value));
              setTranscript([]);
              setCoverage(null);
              setSessionId(null);
            }}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {isComplete && (
            <button
              onClick={() => activate.mutate()}
              disabled={activate.isPending}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {activate.isPending ? "Activating…" : "Activate budget →"}
            </button>
          )}
        </div>
      </div>

      {/* Main: chat + sidebar */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px] overflow-hidden">
        {/* Chat column */}
        <div className="flex flex-col border-r border-slate-200 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3">
            {transcript.length === 0 && sessionQuery.isLoading && (
              <div className="text-sm text-slate-400">Loading your budget session…</div>
            )}
            {transcript.map((turn, i) => (
              <BudgetBubble key={i} turn={turn} />
            ))}
            {sendMessage.isPending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2 text-sm text-slate-500 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-400">
                  <span className="animate-pulse">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 bg-white px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
                placeholder="e.g. 'Revenue will grow 25%' or 'We're hiring 2 engineers in April at $150k each'"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void onSubmit();
                  }
                }}
                disabled={sendMessage.isPending}
              />
              <button
                onClick={() => void onSubmit()}
                disabled={sendMessage.isPending || !draft.trim()}
                className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 disabled:opacity-50"
              >
                Send
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              Describe a driver or ask to revise. The agent computes the amounts — you never type numbers.
            </p>
          </div>
        </div>

        {/* Sidebar — live budget grid */}
        <aside className="bg-white p-6 overflow-y-auto dark:bg-gray-900 space-y-6">
          {/* Coverage summary */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Progress
              </h2>
              {coverage && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {coverage.covered}/{coverage.total} accounts
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="h-2 rounded-full bg-slate-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>

            {coverage && (
              <div className="mt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Total budgeted</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    ${Number(coverage.total_budgeted).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                {coverage.stage && coverage.stage !== "done" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Stage</span>
                    <span className="text-blue-600 font-medium capitalize">{coverage.stage}</span>
                  </div>
                )}
                {isComplete && (
                  <div className="mt-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700 dark:bg-green-950 dark:border-green-800 dark:text-green-400">
                    ✓ All accounts covered — ready to activate
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Open accounts */}
          {coverage && coverage.open_accounts.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 dark:text-slate-500">
                Still open
              </h3>
              <ul className="space-y-1">
                {coverage.open_accounts.map((name) => (
                  <li key={name} className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                    {name}
                  </li>
                ))}
                {coverage.total - coverage.covered > coverage.open_accounts.length && (
                  <li className="text-xs text-slate-400 dark:text-slate-500">
                    +{coverage.total - coverage.covered - coverage.open_accounts.length} more
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Budget-vs-actual (once budget has lines) */}
          {bvaQuery.data?.has_budget && bvaQuery.data.lines.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 dark:text-slate-500">
                Budget vs Actual (annual)
              </h3>
              <div className="space-y-1">
                {bvaQuery.data.lines
                  .filter(l => l.period_month === 0)
                  .sort((a, b) => Math.abs(Number(b.variance)) - Math.abs(Number(a.variance)))
                  .slice(0, 8)
                  .map((line) => {
                    const variance = Number(line.variance);
                    const isOver = variance > 0;
                    const isUnder = variance < 0;
                    return (
                      <div key={line.account_name} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 dark:text-slate-400 truncate max-w-[150px]">
                          {line.account_name}
                        </span>
                        <span className={[
                          "font-medium tabular-nums",
                          isOver ? "text-amber-600" : isUnder ? "text-green-600" : "text-slate-500",
                        ].join(" ")}>
                          {isOver ? "+" : ""}{variance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-gray-800">
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-slate-700 dark:text-slate-300">Net income</span>
                  <div className="text-right">
                    <div className="text-slate-900 dark:text-slate-100">
                      ${Number(bvaQuery.data.net_income_actual).toLocaleString(undefined, { maximumFractionDigits: 0 })} actual
                    </div>
                    <div className="text-xs text-slate-400">
                      vs ${Number(bvaQuery.data.net_income_budget).toLocaleString(undefined, { maximumFractionDigits: 0 })} plan
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Google Sheets import */}
          <SheetsImport
            fiscalYear={fiscalYear}
            onImported={(msg) => {
              setTranscript((t) => [...t, { kind: "system", text: msg }]);
              qc.invalidateQueries({ queryKey: ["budget-session", fiscalYear] });
              qc.invalidateQueries({ queryKey: ["budget-vs-actual", fiscalYear] });
              // Refresh coverage via the agent
              sendMessage.mutate("get_coverage check — how many accounts are now covered?");
            }}
          />

          {/* Quick actions */}
          <div className="pt-2 border-t border-slate-100 dark:border-gray-800 space-y-2">
            <button
              onClick={() => {
                const msg = "Generate a budget from last year with 8% growth";
                setTranscript((t) => [...t, { kind: "user", text: msg }]);
                sendMessage.mutate(msg);
              }}
              disabled={sendMessage.isPending}
              className="w-full text-left text-xs text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
            >
              ↗ Generate from prior year (+8%)
            </button>
            <button
              onClick={() => {
                const msg = "Where are we? What accounts are still open?";
                setTranscript((t) => [...t, { kind: "user", text: msg }]);
                sendMessage.mutate(msg);
              }}
              disabled={sendMessage.isPending}
              className="w-full text-left text-xs text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
            >
              ↗ Where are we? Show open accounts
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ── Google Sheets import component ───────────────────────────────────────────

function SheetsImport({
  fiscalYear,
  onImported,
}: {
  fiscalYear: number;
  onImported: (message: string) => void;
}) {
  const toast = useToast();
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);

  const importMutation = useMutation({
    mutationFn: (sheetUrl: string) =>
      api<{ written: number; unmatched_codes: string[]; message: string }>(
        "/api/fpa/budget/import-from-url",
        {
          method: "POST",
          body: { url: sheetUrl, fiscal_year: fiscalYear },
        }
      ),
    onSuccess: (data) => {
      setUrl("");
      setOpen(false);
      onImported(`✓ ${data.message}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!open) {
    return (
      <div className="pt-2 border-t border-slate-100 dark:border-gray-800">
        <button
          onClick={() => setOpen(true)}
          className="w-full text-left text-xs text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
        >
          📊 Import from Google Sheets
        </button>
      </div>
    );
  }

  return (
    <div className="pt-2 border-t border-slate-100 dark:border-gray-800 space-y-2">
      <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
        Import from Google Sheets
      </p>
      <p className="text-[11px] text-slate-400 dark:text-slate-500">
        Sheet must be shared as "Anyone with the link can view". Supports wide
        (Jan–Dec columns) or long (account/month/amount) format.
      </p>
      <input
        type="url"
        autoFocus
        placeholder="https://docs.google.com/spreadsheets/d/…"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        onKeyDown={(e) => {
          if (e.key === "Enter" && url.trim()) importMutation.mutate(url.trim());
          if (e.key === "Escape") setOpen(false);
        }}
      />
      <div className="flex gap-2">
        <button
          onClick={() => url.trim() && importMutation.mutate(url.trim())}
          disabled={importMutation.isPending || !url.trim()}
          className="flex-1 rounded-md bg-blue-600 text-white text-xs py-1.5 hover:bg-blue-700 disabled:opacity-50"
        >
          {importMutation.isPending ? "Importing…" : "Import"}
        </button>
        <button
          onClick={() => { setOpen(false); setUrl(""); }}
          className="rounded-md border border-slate-300 text-slate-500 text-xs px-3 py-1.5 hover:bg-slate-50 dark:border-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


// ── Chat bubble ────────────────────────────────────────────────────────────────

function BudgetBubble({ turn }: { turn: Turn }) {
  if (turn.kind === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[70%] bg-blue-600 text-white rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap">
          {turn.text}
        </div>
      </div>
    );
  }
  if (turn.kind === "system") {
    return (
      <div className="text-center text-xs text-slate-500 italic dark:text-slate-400 py-1">
        {turn.text}
      </div>
    );
  }
  // agent
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] bg-white border border-slate-200 rounded-2xl px-4 py-2 text-sm text-slate-800 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-200">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 mb-1">
          Budget Agent
        </div>
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.text}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
